/************************************************************************************
 * DEPRECATION NOTICE: This file contains deprecated methods for generating config
 * objects. These methods are retained for backwards compatibility and will be removed
 * in a future release. These methods are retained solely for backwards compatibility.
 *
 * Current release: v4.5.1
 * Deprecation target: v5.0.0
 ************************************************************************************/

import { ConfigurationBuilder } from './config-builder';
import {
    AssetUrlResolver,
    BlurConfig,
    BlurStrength,
    Generator,
    PipelineConfig,
    PreloadConfig,
    Quality,
    RenderConfig,
    ReplacementConfig,
} from './config-types';
import { logger } from './logger';

function logDeprecationWarning(functionName: string): void {
    logger.warn(
        `Deprecation Warning: ${functionName} is deprecated and will be removed in a future release. Please migrate to ConfigurationBuilder for future use.`
    );
}

/**
 * Generates a blur configuration based on the specified blur strength.
 * This function uses the static method from ConfigurationBuilder.
 * @param blurStrength The desired blur strength.
 * @param horizontalMirror Whether to apply horizontal mirroring.
 */
export const getBlurConfig = (
    blurStrength: BlurStrength,
    horizontalMirror?: boolean
): BlurConfig => {
    logDeprecationWarning('getBlurConfig');
    return ConfigurationBuilder.getBlurConfig(blurStrength, horizontalMirror);
};

/**
 * Retrieves a PreloadConfig using the ConfigurationBuilder.
 * The function preserves the old signature for compatibility.
 * @param quality The desired model quality.
 * @param generator The mask generator ('local' or 'worker').
 * @param modelUrlResolver A function to resolve model URLs.
 * @param baseUrl The base URL to use.
 */
export const getPreloadConfig = (
    quality: Quality,
    generator: Generator,
    modelUrlResolver: AssetUrlResolver,
    baseUrl: string
): PreloadConfig => {
    logDeprecationWarning('getPreloadConfig');
    const builder = new ConfigurationBuilder()
        .setBaseUrl(baseUrl)
        .setMaskQuality(quality)
        .setMaskGenerator(generator)
        .setAssetUrlResolver(modelUrlResolver);

    // Build the preload configuration
    const config = builder.build(false);

    return {
        assetUrlResolver: config.assetUrlResolver,
        baseUrl: config.baseUrl,
        wasmUri: config.wasmUri,
        mask: config.mask,
    };
};

/**
 * Retrieves a PipelineConfig using the ConfigurationBuilder.
 * The function preserves the old signature for compatibility.
 * @param height The height of the input size.
 * @param width The width of the input size.
 * @param quality The desired model quality.
 * @param generator The mask generator ('local' or 'worker').
 * @param renderConfig The render configuration (blur, replacement, etc.).
 * @param modelUrlResolver A function to resolve model URLs.
 * @param baseUrl The base URL to use.
 */
export const getConfig = (
    height: number,
    width: number,
    quality: Quality,
    generator: Generator,
    renderConfig: BlurConfig | RenderConfig | ReplacementConfig,
    modelUrlResolver: AssetUrlResolver,
    baseUrl: string
): PipelineConfig => {
    logDeprecationWarning('getConfig');
    const builder = new ConfigurationBuilder()
        .setBaseUrl(baseUrl)
        .setMaskQuality(quality)
        .setInputSize(height, width)
        .setRenderConfig(renderConfig)
        .setMaskGenerator(generator)
        .setAssetUrlResolver(modelUrlResolver);

    // Build the full configuration
    return builder.build() as PipelineConfig;
};
